// Content Filler (/volunteer/content).
//
// Study spots, libraries, microwaves and restaurants: pick one on the left,
// change it on the right, Save. Plus "Highlights & specials", which is the
// Weekly Highlights and day-based-items half of RestaurantsAdmin.
//
// ## ⚠️ Every change is an OVERLAY
//
// Places are authored in UMapsTools and snapshotted into places.json, which
// this page can only READ. What a volunteer saves is stored beside it
// (/api/place-edits) field by field, and each client lays it over the authored
// place as it loads (src/utils/placeEdits.js). So:
//
//  · every field shows the AUTHORED value until changed, and a changed field
//    says so and offers "Reset" — which removes the override rather than
//    typing the old value back (typing it back would freeze it: a fix made
//    upstream later would then be masked by a copy of the old one);
//  · photos a volunteer ADDS go straight onto the place (they are the
//    moderation), and authored photos are HIDDEN rather than deleted;
//  · a save is conflict-checked: if somebody else saved this place since it
//    was opened, the save is refused and the page says to reload, rather than
//    quietly erasing their change.
//
// The editor shows only the fields the clients actually render for that kind
// of place — a field nobody sees is a field nobody can tell is wrong.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import RestaurantsAdmin from '../components/RestaurantsAdmin.js';
import { createHttpDataSource } from '../pathfinder/lib/dataSource.js';
import { api, ago, readFileAsDataUrl } from './shared.js';

const snapshot = createHttpDataSource('/Map%20Info/pathfinder/', {
  imagesBase: process.env.REACT_APP_IMAGES_BASE || undefined,
});
const imageUrl = (p) => snapshot.imageUrl(p, 'thumb');

const KINDS = [
  { id: 'study', label: 'Study spots', type: 'study', match: (p) => p.type === 'study' },
  { id: 'library', label: 'Libraries', type: 'library', match: (p) => p.type === 'library' },
  { id: 'microwave', label: 'Microwaves', type: 'amenity', match: (p) => p.type === 'amenity' && p.amenity === 'microwave' },
  { id: 'restaurant', label: 'Restaurants', type: 'restaurant', match: (p) => p.type === 'restaurant' },
  { id: 'highlights', label: 'Highlights & specials' },
];

const AMENITIES = [
  ['plugs', 'Plugs'], ['cushions', 'Cushions'], ['quiet', 'Quiet zone'], ['loud', 'Loud zone'], ['noTables', 'Chairs only'],
];
const CAPACITIES = [['', 'Not set'], ['low', 'Few seats'], ['medium', 'Medium seats'], ['high', 'Many seats']];
const DAYS = [['mon', 'Mon'], ['tue', 'Tue'], ['wed', 'Wed'], ['thu', 'Thu'], ['fri', 'Fri'], ['sat', 'Sat'], ['sun', 'Sun']];
const TAGS = [
  ['vegetarian', 'V', 'Vegetarian'], ['vegan', 'VG', 'Vegan'], ['halal', 'H', 'Halal'],
  ['glutenFree', 'GF', 'Gluten free'], ['dairyFree', 'DF', 'Dairy free'], ['nutFree', 'NF', 'Nut free'],
];
const ITEM_FIELDS = ['name', 'category', 'price', 'notes', 'calories', 'photo', 'tags'];

const clone = (v) => JSON.parse(JSON.stringify(v ?? null));
const emptyDraft = () => ({ fields: {}, menu: { items: {}, added: [], removed: [] }, hiddenImages: [] });
const draftFrom = (edit) => {
  const d = emptyDraft();
  if (!edit) return d;
  d.fields = clone(edit.fields || {});
  if (edit.menu) d.menu = { items: clone(edit.menu.items || {}), added: clone(edit.menu.added || []), removed: [...(edit.menu.removed || [])] };
  d.hiddenImages = [...(edit.hiddenImages || [])];
  return d;
};
const sameTags = (a, b) => TAGS.every(([k]) => !!a?.[k] === !!b?.[k]);

/** Downscale to a sensible JPEG before sending; HEIC and friends pass through. */
async function shrink(file) {
  const raw = await readFileAsDataUrl(file);
  if (!raw || !/^data:image\/(jpeg|png|webp)/i.test(raw)) return raw;
  try {
    const img = await new Promise((resolve, reject) => {
      const i = new Image(); i.onload = () => resolve(i); i.onerror = reject; i.src = raw;
    });
    const max = 1600;
    const scale = Math.min(1, max / Math.max(img.width, img.height));
    if (scale === 1 && raw.length < 1.5e6) return raw;
    const c = document.createElement('canvas');
    c.width = Math.round(img.width * scale); c.height = Math.round(img.height * scale);
    c.getContext('2d').drawImage(img, 0, 0, c.width, c.height);
    return c.toDataURL('image/jpeg', 0.85);
  } catch { return raw; }
}

export default function ContentFiller({ token, onExpired }) {
  const [kind, setKind] = useState('study');
  const [places, setPlaces] = useState(null);
  const [edits, setEdits] = useState({});
  const [photos, setPhotos] = useState({});
  const [err, setErr] = useState('');
  const [selected, setSelected] = useState('');
  const [q, setQ] = useState('');

  const loadOverlay = useCallback(async () => {
    const r = await api('/api/place-edits?part=admin', { token, onExpired });
    if (!r.ok) { setErr(r.body.error || 'Could not load edits.'); return null; }
    setEdits(r.body.edits || {});
    setPhotos(r.body.photos || {});
    return r.body;
  }, [token, onExpired]);

  useEffect(() => {
    let live = true;
    snapshot.loadPlaces()
      .then((list) => { if (live) setPlaces(Array.isArray(list) ? list : []); })
      .catch(() => { if (live) setErr('Could not load the campus places file.'); });
    loadOverlay();
    return () => { live = false; };
  }, [loadOverlay]);

  const kindDef = KINDS.find((k) => k.id === kind);
  const list = useMemo(() => {
    if (!places || !kindDef?.match) return [];
    const needle = q.trim().toLowerCase();
    return places.filter(kindDef.match)
      .map((p) => ({ ...p, _title: edits[p.id]?.fields?.name || p.name || 'Unnamed' }))
      .filter((p) => !needle || `${p._title} ${p.building} ${p.roomNumber} ${p.notes || ''}`.toLowerCase().includes(needle))
      .sort((a, b) => a._title.localeCompare(b._title));
  }, [places, kindDef, q, edits]);

  const place = list.find((p) => p.id === selected) || null;

  return (
    <>
      <h1 className="vt-h1">Content Filler</h1>
      <p className="vt-sub">Fix and fill in what students see about places on campus. Saved changes show on the website within a minute.</p>
      <div className="vt-tabs" role="group" aria-label="What to edit">
        {KINDS.map((k) => (
          <button key={k.id} className="vt-tab" aria-pressed={kind === k.id} onClick={() => { setKind(k.id); setSelected(''); setQ(''); }}>
            {k.label}
            {k.match && places && <span className="vt-count">{places.filter(k.match).length}</span>}
          </button>
        ))}
      </div>
      {err && <div className="vt-err">{err}</div>}

      {kind === 'highlights' ? (
        <>
          <p className="vt-sub">
            <b>Weekly Highlights</b> is the strip at the top of Restaurants in the app (up to five picks, cycled).
            <b> Temporary menu items</b> are day-based specials — a soup of the day, a Friday-only item — shown at the top of that restaurant’s menu only on the days and dates you pick.
          </p>
          <RestaurantsAdmin token={token} onExpired={onExpired} panels={['picks', 'temp']} />
        </>
      ) : !places ? <div className="vt-empty">Loading places…</div> : (
        <div className="vt-split">
          <div className="vt-card vt-side">
            <div style={{ padding: 10 }}>
              <input className="vt-input" placeholder={`Search ${kindDef.label.toLowerCase()}`} value={q} onChange={(e) => setQ(e.target.value)} />
            </div>
            <div className="vt-sidelist">
              {list.map((p) => (
                <button key={p.id} className="vt-sideitem" aria-current={p.id === selected} onClick={() => setSelected(p.id)}>
                  <div style={{ fontWeight: 650 }}>{p._title} {edits[p.id] && <span className="vt-pill vt-pill-warn">Edited</span>}</div>
                  <div className="vt-meta" style={{ marginTop: 0 }}>
                    {[p.building?.replace(/_/g, ' '), p.roomNumber, p.notes && kind === 'microwave' ? p.notes : ''].filter(Boolean).join(' · ') || '—'}
                  </div>
                </button>
              ))}
              {!list.length && <div className="vt-meta" style={{ padding: 12 }}>Nothing matches.</div>}
            </div>
          </div>
          <div>
            {place ? (
              <PlaceEditor key={`${place.id}:${edits[place.id]?.updatedAt || ''}`} place={place} type={kindDef.type}
                           edit={edits[place.id] || null} photos={photos[place.id] || []}
                           token={token} onExpired={onExpired} reload={loadOverlay} />
            ) : (
              <div className="vt-empty">Pick a place on the left to see and change what students see about it.</div>
            )}
          </div>
        </div>
      )}
    </>
  );
}

function FieldHead({ label, changed, onReset, htmlFor }) {
  return (
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
      <label className="vt-label" htmlFor={htmlFor}>{label}</label>
      {changed && <span className="vt-changedmark">CHANGED</span>}
      {changed && <button type="button" className="vt-btn vt-ghost vt-small" style={{ marginLeft: 'auto' }} onClick={onReset}>Reset to original</button>}
    </div>
  );
}

function PlaceEditor({ place, type, edit, photos, token, onExpired, reload }) {
  const [draft, setDraft] = useState(() => draftFrom(edit));
  const baseline = useMemo(() => JSON.stringify(draftFrom(edit)), [edit]);
  const dirty = JSON.stringify(draft) !== baseline;
  const [busy, setBusy] = useState('');
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const [conflict, setConflict] = useState(false);
  const fileRef = useRef(null);

  const has = (k) => Object.prototype.hasOwnProperty.call(draft.fields, k);
  const val = (k, fallback = '') => (has(k) ? draft.fields[k] : (place[k] ?? fallback));
  const setField = (k, v) => setDraft((d) => ({ ...d, fields: { ...d.fields, [k]: v } }));
  const resetField = (k) => setDraft((d) => { const f = { ...d.fields }; delete f[k]; return { ...d, fields: f }; });

  const save = async () => {
    setBusy('save'); setErr(''); setMsg('');
    const overlay = {
      fields: draft.fields,
      ...(type === 'restaurant' ? { menu: draft.menu } : {}),
      hiddenImages: draft.hiddenImages,
    };
    const r = await api('/api/place-edits', {
      method: 'POST', token, onExpired,
      body: { op: 'save', placeId: place.id, type, overlay, expectUpdatedAt: edit?.updatedAt || '' },
    });
    setBusy('');
    if (r.status === 409) { setConflict(true); setErr(r.body.error); return; }
    if (!r.ok) { setErr(r.body.error || 'That did not save.'); return; }
    setMsg('Saved. The website shows it within a minute.');
    await reload();
  };

  const revertAll = async () => {
    if (!window.confirm(`Undo every change to “${place.name}” and go back to the original?`)) return;
    setBusy('revert'); setErr('');
    const r = await api('/api/place-edits', { method: 'POST', token, onExpired, body: { op: 'revert', placeId: place.id } });
    setBusy('');
    if (!r.ok) { setErr(r.body.error || 'That did not go through.'); return; }
    await reload();
  };

  const upload = async (files) => {
    setErr(''); setMsg('');
    for (const f of files) {
      setBusy(`upload:${f.name}`);
      const dataUrl = await shrink(f);
      if (!dataUrl) { setErr(`${f.name} could not be read.`); continue; }
      const r = await api('/api/place-edits', {
        method: 'POST', token, onExpired,
        body: { op: 'upload', placeId: place.id, placeName: place.name, dataUrl, filename: f.name, target: 'gallery' },
      });
      if (!r.ok) { setErr(r.status === 413 ? `${f.name} is too large.` : (r.body.error || `${f.name} did not upload.`)); }
    }
    setBusy('');
    setMsg('Photos added — they are live now.');
    await reload();
  };

  const removePhoto = async (p) => {
    if (!window.confirm('Remove this photo from the place?')) return;
    setBusy(`rm:${p.id}`);
    const r = await api('/api/place-edits', { method: 'POST', token, onExpired, body: { op: 'remove-photo', placeId: place.id, id: p.id } });
    setBusy('');
    if (!r.ok) { setErr(r.body.error || 'That did not go through.'); return; }
    await reload();
  };

  const toggleHidden = (url) => setDraft((d) => ({
    ...d, hiddenImages: d.hiddenImages.includes(url) ? d.hiddenImages.filter((u) => u !== url) : [...d.hiddenImages, url],
  }));

  const where = [place.building?.replace(/_/g, ' '), place.roomNumber && `room ${place.roomNumber}`,
    Number.isFinite(place.floor) ? `floor ${place.floor}` : ''].filter(Boolean).join(' · ');
  const amenities = val('amenities', {}) || {};

  return (
    <div className="vt-card vt-pad">
      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', marginBottom: 14 }}>
        <div className="vt-grow">
          <div className="vt-title" style={{ fontSize: 18 }}>{val('name') || 'Unnamed'}</div>
          <div className="vt-meta">{where || '—'}</div>
          {edit && <div className="vt-hint">Last changed {ago(edit.updatedAt)}{edit.by ? ` by ${edit.by}` : ''}</div>}
        </div>
        <a className="vt-btn vt-small" href={`/umanitoba`} target="_blank" rel="noreferrer">Open the map ↗</a>
      </div>

      {err && <div className="vt-err">{err} {conflict && <button className="vt-btn vt-small" onClick={reload}>Reload</button>}</div>}
      {msg && <div className="vt-ok">{msg}</div>}

      <div className="vt-field">
        <FieldHead label="Name" htmlFor="pe-name" changed={has('name')} onReset={() => resetField('name')} />
        <input id="pe-name" className="vt-input" value={val('name')} onChange={(e) => setField('name', e.target.value)} />
      </div>

      <div className="vt-field">
        <FieldHead label={type === 'restaurant' ? 'Hours (one line)' : 'Hours'} htmlFor="pe-hours" changed={has('hours')} onReset={() => resetField('hours')} />
        <input id="pe-hours" className="vt-input" value={val('hours')} onChange={(e) => setField('hours', e.target.value)}
               placeholder={type === 'restaurant' ? 'e.g. Mon–Fri 8:00 AM – 4:00 PM · Sat–Sun closed' : 'e.g. Mon–Fri 7:30 AM – 10 PM'} />
        {type === 'restaurant' && <div className="vt-hint">The app works out “Open now” from the weekly hours below; this line is the summary shown beside it.</div>}
      </div>

      <div className="vt-field">
        <FieldHead label={type === 'amenity' ? 'Where exactly' : 'Notes'} htmlFor="pe-notes" changed={has('notes')} onReset={() => resetField('notes')} />
        <textarea id="pe-notes" className="vt-textarea" rows={2} value={val('notes')} onChange={(e) => setField('notes', e.target.value)}
                  placeholder={type === 'amenity' ? 'e.g. “Near the food court, beside the recycling bins”' : ''} />
      </div>

      {type === 'study' && (
        <>
          <div className="vt-field">
            <FieldHead label="Seating" htmlFor="pe-cap" changed={has('capacity')} onReset={() => resetField('capacity')} />
            <select id="pe-cap" className="vt-select" style={{ maxWidth: 240 }} value={val('capacity')} onChange={(e) => setField('capacity', e.target.value)}>
              {CAPACITIES.map(([id, label]) => <option key={id} value={id}>{label}</option>)}
            </select>
          </div>
          <div className="vt-field">
            <FieldHead label="What it has" changed={has('amenities')} onReset={() => resetField('amenities')} />
            <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
              {AMENITIES.map(([k, label]) => (
                <label key={k} style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  <input type="checkbox" checked={!!amenities[k]}
                         onChange={(e) => setField('amenities', { ...amenities, [k]: e.target.checked })} />{label}
                </label>
              ))}
            </div>
          </div>
        </>
      )}

      {type === 'restaurant' && (
        <HoursEditor value={val('hoursByDay', null)} changed={has('hoursByDay')}
                     onChange={(v) => setField('hoursByDay', v)} onReset={() => resetField('hoursByDay')} />
      )}

      <PhotoEditor place={place} photos={photos} hidden={draft.hiddenImages} busy={busy}
                   onToggleHidden={toggleHidden} onRemove={removePhoto}
                   onPick={() => fileRef.current?.click()} />
      <input ref={fileRef} type="file" accept="image/*" multiple hidden
             onChange={(e) => { const fs = [...(e.target.files || [])]; e.target.value = ''; if (fs.length) upload(fs); }} />

      {type === 'restaurant' && (
        <MenuEditor place={place} menu={draft.menu} token={token} onExpired={onExpired}
                    onChange={(menu) => setDraft((d) => ({ ...d, menu }))} />
      )}

      <div className="vt-savebar">
        <div className="vt-grow">{dirty ? 'You have unsaved changes.' : edit ? 'All changes saved.' : 'Showing the original information.'}</div>
        {edit && <button className="vt-btn vt-danger" disabled={!!busy} onClick={revertAll}>Undo all changes</button>}
        <button className="vt-btn" disabled={!dirty || !!busy} onClick={() => { setDraft(draftFrom(edit)); setErr(''); }}>Discard</button>
        <button className="vt-btn vt-primary" disabled={!dirty || !!busy} onClick={save}>{busy === 'save' ? 'Saving…' : 'Save changes'}</button>
      </div>
    </div>
  );
}

function HoursEditor({ value, changed, onChange, onReset }) {
  const v = value || {};
  const day = (k) => v[k] || { open: '', close: '', closed: false };
  const set = (k, patch) => {
    const next = {};
    for (const [d] of DAYS) next[d] = { ...day(d), ...(d === k ? patch : {}) };
    onChange(next);
  };
  const copyWeekdays = () => {
    const mon = day('mon');
    const next = {};
    for (const [d] of DAYS) next[d] = ['tue', 'wed', 'thu', 'fri'].includes(d) ? { ...mon } : { ...day(d) };
    onChange(next);
  };
  return (
    <div className="vt-field">
      <FieldHead label="Weekly hours" changed={changed} onReset={onReset} />
      <div className="vt-hours">
        {DAYS.map(([k, label]) => {
          const d = day(k);
          return [
            <b key={`${k}l`}>{label}</b>,
            <input key={`${k}o`} type="time" className="vt-input" aria-label={`${label} opens`} disabled={d.closed}
                   value={d.open} onChange={(e) => set(k, { open: e.target.value })} />,
            <input key={`${k}c`} type="time" className="vt-input" aria-label={`${label} closes`} disabled={d.closed}
                   value={d.close} onChange={(e) => set(k, { close: e.target.value })} />,
            <label key={`${k}x`} style={{ display: 'flex', gap: 5, alignItems: 'center', fontSize: 13 }}>
              <input type="checkbox" checked={d.closed} onChange={(e) => set(k, { closed: e.target.checked })} />Closed
            </label>,
          ];
        })}
      </div>
      <div className="vt-hint">
        Blank times mean “we don’t know” (the app says Hours unknown) — not closed. A closing time earlier than the
        opening time runs past midnight. <button type="button" className="vt-btn vt-ghost vt-small" onClick={copyWeekdays}>Copy Monday to Tue–Fri</button>
      </div>
    </div>
  );
}

function PhotoEditor({ place, photos, hidden, busy, onToggleHidden, onRemove, onPick }) {
  const authored = Array.isArray(place.images) ? place.images : [];
  return (
    <div className="vt-field">
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
        <span className="vt-label">Photos</span>
        <button type="button" className="vt-btn vt-small" style={{ marginLeft: 'auto' }} disabled={!!busy} onClick={onPick}>
          {String(busy).startsWith('upload:') ? 'Uploading…' : '+ Add photos'}
        </button>
      </div>
      {!authored.length && !photos.length ? <div className="vt-meta">No photos yet.</div> : (
        <div className="vt-photos">
          {photos.map((p) => (
            <div key={p.id} className="vt-photo">
              <img src={imageUrl(p.url)} alt="" loading="lazy" />
              <div className="vt-photobar">
                <span>{p.source === 'volunteer' ? 'Added here' : 'Student photo'}</span>
                <button type="button" disabled={busy === `rm:${p.id}`} onClick={() => onRemove(p)}>Remove</button>
              </div>
            </div>
          ))}
          {authored.map((u) => {
            const off = hidden.includes(u);
            return (
              <div key={u} className={`vt-photo${off ? ' vt-photo-hidden' : ''}`}>
                <img src={imageUrl(u)} alt="" loading="lazy" />
                <div className="vt-photobar">
                  <span>{off ? 'Hidden' : 'Original'}</span>
                  <button type="button" onClick={() => onToggleHidden(u)}>{off ? 'Show' : 'Hide'}</button>
                </div>
              </div>
            );
          })}
        </div>
      )}
      <div className="vt-hint">Added photos go live straight away. Hiding an original takes effect when you save. Don’t upload photos with people’s faces in them.</div>
    </div>
  );
}

function MenuEditor({ place, menu, token, onExpired, onChange }) {
  const authored = Array.isArray(place.menuItems) ? place.menuItems : [];
  const [q, setQ] = useState('');
  const [uploading, setUploading] = useState('');
  const [err, setErr] = useState('');
  const fileRef = useRef(null);
  const target = useRef(null);

  const current = (it) => {
    const p = menu.items[it.id] || {};
    const out = { ...it };
    for (const f of ITEM_FIELDS) if (Object.prototype.hasOwnProperty.call(p, f)) out[f] = p[f];
    return out;
  };
  const setItem = (it, field, value) => {
    const patch = { ...(menu.items[it.id] || {}) };
    const same = field === 'tags' ? sameTags(value, it.tags) : String(value ?? '') === String(it[field] ?? '');
    if (same) delete patch[field]; else patch[field] = value;
    const items = { ...menu.items };
    if (Object.keys(patch).length) items[it.id] = patch; else delete items[it.id];
    onChange({ ...menu, items });
  };
  const toggleRemoved = (it) => onChange({
    ...menu, removed: menu.removed.includes(it.id) ? menu.removed.filter((x) => x !== it.id) : [...menu.removed, it.id],
  });
  const setAdded = (i, patch) => onChange({ ...menu, added: menu.added.map((a, n) => (n === i ? { ...a, ...patch } : a)) });
  const addItem = () => onChange({ ...menu, added: [...menu.added, { name: '', category: '', price: '', notes: '', calories: '', photo: '', tags: {} }] });
  const dropAdded = (i) => onChange({ ...menu, added: menu.added.filter((_, n) => n !== i) });

  const pickPhoto = (setter) => { target.current = setter; fileRef.current?.click(); };
  const onFile = async (file) => {
    const setter = target.current;
    if (!file || !setter) return;
    setUploading(file.name); setErr('');
    const dataUrl = await shrink(file);
    const r = await api('/api/place-edits', {
      method: 'POST', token, onExpired,
      body: { op: 'upload', placeId: place.id, dataUrl, filename: file.name, target: 'item' },
    });
    setUploading('');
    if (!r.ok) { setErr(r.body.error || 'That photo did not upload.'); return; }
    setter(r.body.url);
  };

  const needle = q.trim().toLowerCase();
  const rows = authored.filter((it) => !needle || `${current(it).name} ${current(it).category}`.toLowerCase().includes(needle));
  const changedCount = Object.keys(menu.items).length + menu.removed.length + menu.added.length;

  const tagCell = (tags, onTags) => (
    <div style={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
      {TAGS.map(([k, short, label]) => (
        <button key={k} type="button" title={label} aria-pressed={!!tags?.[k]} className="vt-chip"
                style={{ padding: '1px 6px', fontSize: 11, ...(tags?.[k] ? { background: 'var(--goodSoft)', color: 'var(--good)', borderColor: '#a8dab5' } : {}) }}
                onClick={() => onTags({ ...(tags || {}), [k]: !tags?.[k] })}>{short}</button>
      ))}
    </div>
  );
  const photoCell = (photo, set) => (
    <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
      {photo ? <img src={imageUrl(photo)} alt="" style={{ width: 34, height: 34, objectFit: 'cover', borderRadius: 6 }} /> : null}
      <button type="button" className="vt-btn vt-small" onClick={() => pickPhoto(set)}>{photo ? 'Change' : 'Add'}</button>
      {photo && <button type="button" className="vt-btn vt-ghost vt-small" onClick={() => set('')}>✕</button>}
    </div>
  );

  return (
    <div className="vt-field" style={{ marginTop: 18 }}>
      <input ref={fileRef} type="file" accept="image/*" hidden onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; onFile(f); }} />
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap', marginBottom: 6 }}>
        <span className="vt-label">Menu</span>
        {changedCount > 0 && <span className="vt-changedmark">{changedCount} CHANGE{changedCount === 1 ? '' : 'S'}</span>}
        <input className="vt-input" style={{ maxWidth: 240, marginLeft: 'auto' }} placeholder="Find an item" value={q} onChange={(e) => setQ(e.target.value)} />
      </div>
      {err && <div className="vt-err">{err}</div>}
      {uploading && <div className="vt-note">Uploading {uploading}…</div>}
      <div className="vt-hint" style={{ marginBottom: 8 }}>
        Prices are text: leave blank if there’s no printed price (blank is not free). Items priced by option (e.g. by size) keep their options — change those in UMapsTools.
        Day-based specials go under <b>Highlights &amp; specials</b>.
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table className="vt-menu">
          <thead>
            <tr><th style={{ minWidth: 150 }}>Item</th><th style={{ minWidth: 110 }}>Category</th><th style={{ width: 80 }}>Price</th>
              <th style={{ minWidth: 170 }}>Description</th><th>Dietary</th><th>Photo</th><th /></tr>
          </thead>
          <tbody>
            {rows.map((it) => {
              const c = current(it);
              const removed = menu.removed.includes(it.id);
              return (
                <tr key={it.id} className={removed ? 'vt-removed' : menu.items[it.id] ? 'vt-changed' : ''}>
                  <td><input className="vt-input" value={c.name || ''} disabled={removed} onChange={(e) => setItem(it, 'name', e.target.value)} /></td>
                  <td><input className="vt-input" value={c.category || ''} disabled={removed} onChange={(e) => setItem(it, 'category', e.target.value)} /></td>
                  <td>
                    <input className="vt-input" value={c.price || ''} disabled={removed} onChange={(e) => setItem(it, 'price', e.target.value)}
                           placeholder={it.options ? 'by option' : ''} />
                  </td>
                  <td><textarea className="vt-input" rows={1} style={{ resize: 'vertical' }} value={c.notes || ''} disabled={removed}
                                onChange={(e) => setItem(it, 'notes', e.target.value)} /></td>
                  <td>{tagCell(c.tags, (t) => setItem(it, 'tags', t))}</td>
                  <td>{photoCell(c.photo, (u) => setItem(it, 'photo', u))}</td>
                  <td>
                    <button type="button" className="vt-btn vt-small" onClick={() => toggleRemoved(it)}>{removed ? 'Show' : 'Hide'}</button>
                    {menu.items[it.id] && !removed && (
                      <button type="button" className="vt-btn vt-ghost vt-small" title="Undo changes to this item"
                              onClick={() => { const items = { ...menu.items }; delete items[it.id]; onChange({ ...menu, items }); }}>↺</button>
                    )}
                  </td>
                </tr>
              );
            })}
            {menu.added.map((a, i) => (
              <tr key={a.id || `new-${i}`} className="vt-changed">
                <td><input className="vt-input" value={a.name} placeholder="New item" onChange={(e) => setAdded(i, { name: e.target.value })} /></td>
                <td><input className="vt-input" value={a.category} onChange={(e) => setAdded(i, { category: e.target.value })} /></td>
                <td><input className="vt-input" value={a.price} onChange={(e) => setAdded(i, { price: e.target.value })} /></td>
                <td><textarea className="vt-input" rows={1} value={a.notes} onChange={(e) => setAdded(i, { notes: e.target.value })} /></td>
                <td>{tagCell(a.tags, (t) => setAdded(i, { tags: t }))}</td>
                <td>{photoCell(a.photo, (u) => setAdded(i, { photo: u }))}</td>
                <td><button type="button" className="vt-btn vt-danger vt-small" onClick={() => dropAdded(i)}>Remove</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {!authored.length && !menu.added.length && <div className="vt-meta" style={{ padding: '8px 0' }}>No menu has been entered for this restaurant yet.</div>}
      <button type="button" className="vt-btn vt-small" style={{ marginTop: 8 }} onClick={addItem}>+ Add a menu item</button>
    </div>
  );
}
